import type { AudioEngine } from "../AudioEngine";

// RiserFX: a 1-2 bar build-up sweep that crescendoes into a drop. Two
// elements layered together:
//   1. White noise -> bandpass that opens from ~400Hz to ~6kHz with
//      rising volume.
//   2. A sawtooth oscillator pitching up over the same time window with
//      a synchronised volume ramp.
// The combined effect is the classic "sshhhhWHOOOOSH" build that primes
// the listener for the downbeat of the drop.
//
// Triggered explicitly by `MusicDirector` on `build -> drop` section
// transitions. We don't auto-loop or schedule -- it's a one-shot.

export class RiserFX {
  private engine: AudioEngine;
  private noiseBuffer: AudioBuffer | null = null;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  // Trigger a riser. `time` is the AudioContext time at which the riser
  // starts; `durationSec` controls how long the build takes (1-3 sec
  // is the sweet spot for a 2-bar build at 140 BPM).
  trigger(time: number, durationSec = 1.7, gain = 0.6): void {
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;
    if (!this.noiseBuffer) this.noiseBuffer = makeNoise(ctx, durationSec + 0.5);

    // Noise sweep
    const n = ctx.createBufferSource();
    n.buffer = this.noiseBuffer;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.4;
    bp.frequency.setValueAtTime(400, time);
    bp.frequency.exponentialRampToValueAtTime(6000, time + durationSec);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, time);
    ng.gain.exponentialRampToValueAtTime(gain * 0.7, time + durationSec);
    // Sharp cut at the drop downbeat -- that "ssh-WHOOSH-silence-BOOM"
    // gap is what makes the impact land.
    ng.gain.linearRampToValueAtTime(0.0001, time + durationSec + 0.04);
    n.connect(bp).connect(ng).connect(dest);
    n.start(time);
    n.stop(time + durationSec + 0.1);

    // Pitched saw
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(120, time);
    o.frequency.exponentialRampToValueAtTime(900, time + durationSec);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, time);
    og.gain.exponentialRampToValueAtTime(gain * 0.4, time + durationSec);
    og.gain.linearRampToValueAtTime(0.0001, time + durationSec + 0.04);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2400, time);
    lp.frequency.exponentialRampToValueAtTime(8000, time + durationSec);
    o.connect(lp).connect(og).connect(dest);
    o.start(time);
    o.stop(time + durationSec + 0.1);
  }
}

function makeNoise(ctx: AudioContext, durationSec: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}
